import { useEffect, useMemo, useState } from 'react'
import MultiLocationSelector from 'app/components/MultiLocationSelector'
import RequiredLocationSelector from 'app/components/RequiredLocationSelector'
import { legacyRoleRank } from 'app/types/roles'
import type { LocationJson, LocationType } from 'app/types/entities'
import type { UserJson } from 'app/types/entities'
import { SearchResponse } from 'app/services/search'
import { useUser } from 'app/utils'
import { bg } from 'lib/background'
import { debug } from 'lib/logging'
import { gqlFetchList } from 'app/lib/graphqlClient'

type LocationUser = Partial<Pick<UserJson, 'role' | 'allowedLocationIds' | 'assignedLocationIds' | 'workspaces'>>

type LocationParams = {
  isNewUser?: boolean
  isNewWorkspaceUser?: boolean
  staffV2Enabled: boolean
  user: LocationUser
  currentWorkspaceId?: string
}

/** Resolve the location IDs stored for the current workspace. */
const resolveLocationIds = (user: LocationUser, currentWorkspaceId?: string): string[] | undefined => {
  // workspaces[] is the source of truth per workspace; no fallback to
  // allowedLocationIds, which would leak another workspace's locations.
  if (user.workspaces?.length) {
    return user.workspaces.find((w) => w.id === currentWorkspaceId)?.locationIds
  }
  return user.allowedLocationIds ?? undefined
}

/** Determine whether the location selector should show "All Locations" on initial render */
export const getInitialAllLocations = ({
  isNewUser,
  isNewWorkspaceUser,
  staffV2Enabled,
  user,
  currentWorkspaceId,
}: LocationParams): boolean => {
  if (isNewUser) return true

  if (staffV2Enabled) return !resolveLocationIds(user, currentWorkspaceId)?.length

  if (user.role! >= legacyRoleRank.orgUser) return !user.assignedLocationIds?.length
  if (user.role === legacyRoleRank.workspaceOwner) return true

  return !isNewWorkspaceUser && !user.allowedLocationIds?.length
}

/** Get the location IDs to load for the current workspace context */
export const getLocationIdsToLoad = ({
  staffV2Enabled,
  user,
  currentWorkspaceId,
}: LocationParams): string[] | undefined => {
  if (staffV2Enabled) {
    const ids = resolveLocationIds(user, currentWorkspaceId)
    return ids?.length ? ids : undefined
  }

  return user.allowedLocationIds?.length
    ? user.allowedLocationIds
    : user.assignedLocationIds?.length
      ? user.assignedLocationIds
      : undefined
}

/** Scope dropdown options to the locations the viewer can grant (empty = all). */
export const getLocationOptionsFilter = ({
  viewerAllowedLocationIds,
}: {
  viewerAllowedLocationIds: string[] | undefined
}): { id: string } | undefined =>
  viewerAllowedLocationIds?.length
    ? { id: `(${viewerAllowedLocationIds.map((id) => `"${id}"`).join(` OR `)})` }
    : undefined

type Props = {
  user: UserJson
  role: number
  isNewUser: boolean
  isNewWorkspaceUser: boolean
  isOrgLevel: boolean
  staffV2Enabled: boolean
  setIsDirtyForm: (dirty: boolean) => void
  onHiddenInputsChange?: (inputs: Array<{ name: string; value: string }>) => void
  disabled?: boolean
}

export default function UserLocationSelector({
  user,
  role,
  isNewUser,
  isNewWorkspaceUser,
  isOrgLevel,
  staffV2Enabled,
  setIsDirtyForm,
  onHiddenInputsChange,
  disabled,
}: Props) {
  const me = useUser()

  const [allowedLocations, setAllowedLocations] = useState<LocationType[]>([])
  const [allLocations, setAllLocations] = useState(() =>
    getInitialAllLocations({
      isNewUser,
      isNewWorkspaceUser,
      staffV2Enabled,
      user,
      currentWorkspaceId: me?.workspaceId,
    }),
  )

  async function fetchLocations(locationIds: string[]): Promise<SearchResponse<LocationJson>> {
    const result = await gqlFetchList<LocationJson>(
      'locations',
      { search: `id:(${locationIds.map((id) => `"${id}"`).join(` OR `)})` },
      'id name orgId workspaceId',
    )
    return { hits: result.hits as any, total: result.total, page: result.page }
  }

  const allLocationsNow = getInitialAllLocations({
    isNewUser,
    isNewWorkspaceUser,
    staffV2Enabled,
    user,
    currentWorkspaceId: me?.workspaceId,
  })
  const resolvedLocationIds = getLocationIdsToLoad({
    staffV2Enabled,
    user,
    currentWorkspaceId: me?.workspaceId,
  })
  const resolvedLocationIdsKey = resolvedLocationIds?.join(`,`) ?? ``

  useEffect(() => {
    if (!me) return
    const loadLocations = async () => {
      if (!allLocationsNow && resolvedLocationIds?.length) {
        debug(`location`, `loading locationIds`, resolvedLocationIds)
        const response = await fetchLocations(resolvedLocationIds)
        if (response?.hits) {
          setAllLocations(false)
          setAllowedLocations(response.hits as LocationType[])
          return
        }
      }
      setAllLocations(allLocationsNow)
      if (allLocationsNow) setAllowedLocations([])
    }
    bg(loadLocations())
  }, [me?.workspaceId, allLocationsNow, resolvedLocationIdsKey])

  useEffect(() => {
    if (onHiddenInputsChange) {
      const hiddenInputs = allowedLocations.map((l) => ({ name: `allowedLocationIds`, value: l?.id || `` }))
      onHiddenInputsChange(hiddenInputs)
    }
  }, [allowedLocations])

  // Memoize to prevent infinite re-renders in ComboMultiSelectAsync's additionalParams effect
  const filterLocations = useMemo(
    () => getLocationOptionsFilter({ viewerAllowedLocationIds: me?.allowedLocationIds }),
    [me?.allowedLocationIds],
  )

  if (!me) return null

  const onAllowedLocationChange = (location: LocationJson, index?: number) => {
    if (allowedLocations.some((allowedLocation) => allowedLocation.id === location.id)) {
      removeAllowedLocation(location)
    } else {
      setAllowedLocations((prev) => [...prev, location as LocationType])
    }
    setIsDirtyForm(true)
  }

  const removeAllowedLocation = (location: LocationJson) => {
    setAllowedLocations((prev) => prev.filter((item) => item.id !== location.id))
    setIsDirtyForm(true)
  }

  const handleAllLocationsChange = (isOn: boolean) => {
    setAllLocations(isOn)
    if (isOn !== allLocations) {
      setIsDirtyForm(true)
    }
  }

  const handleLocationChangeV2 = async (locationIds: string[]) => {
    debug(`location`, `handleLocationChange`, locationIds)
    setIsDirtyForm(true)

    if (locationIds.length === 0) {
      setAllLocations(true)
      setAllowedLocations([])
    } else {
      setAllLocations(false)
      const response = await fetchLocations(locationIds)
      if (response?.hits) {
        setAllowedLocations(response.hits as LocationType[])
      }
    }
  }

  const commonPropsV1 = {
    user,
    role,
    allLocations,
    allowedLocations,
    setAllLocations,
    setAllowedLocations,
    isNewWorkspaceUser,
    isOrgLevel,
    getFilterLocations: filterLocations,
  }

  const commonPropsV2 = {
    allLocations,
    allowedLocations,
    isOrgLevel,
    getFilterLocations: filterLocations,
  }

  return staffV2Enabled ? (
    <MultiLocationSelector {...commonPropsV2} onLocationChange={handleLocationChangeV2} disabled={disabled} />
  ) : (
    <RequiredLocationSelector
      {...commonPropsV1}
      onAllowedLocationChange={onAllowedLocationChange}
      removeAllowedLocation={removeAllowedLocation}
      handleAllLocationsChange={handleAllLocationsChange}
    />
  )
}
